import { useState } from 'react';
import { Plus, X } from 'lucide-react';

/**
 * La saisie d'une liste de tailles : une pastille par taille, un champ pour
 * en ajouter.
 *
 * Le geste vient du bloc « Tailles de vêtements » des Paramètres, et il vaut
 * partout où l'on décline un article : on voit ce qui est déclaré, on retire
 * d'un clic, on ajoute au fil de la frappe. La saisie en une ligne séparée par
 * des virgules qu'utilisait la boutique demandait de relire sa propre
 * ponctuation pour savoir ce qui était enregistré — et une virgule oubliée
 * fabriquait une taille « L XL » que personne ne remarquait avant la première
 * commande.
 *
 * Le composant ne s'enregistre pas lui-même : il rend la liste à son parent,
 * qui décide quand la poser.
 */
export default function SaisieTailles({
  tailles,
  onChange,
  placeholder = 'Taille…',
  /** Proposées d'un clic quand la liste est vide : plus vite retirées que retapées. */
  suggestions = ['S', 'M', 'L', 'XL', 'XXL'],
  desactive = false,
}: {
  tailles: string[];
  onChange: (tailles: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
  desactive?: boolean;
}) {
  const [saisie, setSaisie] = useState('');

  const ajouter = () => {
    // Une taille collée depuis un tableur arrive parfois en « S, M, L » :
    // autant l'accepter que de la faire retaper terme à terme.
    const nouvelles = saisie
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t !== '' && !tailles.includes(t));

    if (nouvelles.length === 0) {
      setSaisie('');
      return;
    }

    onChange([...tailles, ...nouvelles]);
    setSaisie('');
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tailles.map((taille) => (
        <span
          key={taille}
          className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-300"
        >
          {taille}
          {!desactive && (
            <button
              type="button"
              onClick={() => onChange(tailles.filter((t) => t !== taille))}
              className="text-gray-400 hover:text-red-600"
              title={`Retirer ${taille}`}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </span>
      ))}

      {!desactive && (
        <span className="inline-flex items-center gap-1">
          <input
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                ajouter();
              }
              // Retour arrière sur un champ vide : on retire la dernière
              // pastille, comme le ferait n'importe quel champ à étiquettes.
              if (e.key === 'Backspace' && saisie === '' && tailles.length > 0) {
                onChange(tailles.slice(0, -1));
              }
            }}
            onBlur={ajouter}
            placeholder={placeholder}
            className="w-24 rounded-full border border-dashed border-gray-300 dark:border-gray-600 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:border-primary-500"
          />
          <button
            type="button"
            onClick={ajouter}
            className="p-1 text-primary-600 hover:text-primary-800"
            title="Ajouter la taille"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </span>
      )}

      {!desactive && tailles.length === 0 && suggestions.length > 0 && (
        <button
          type="button"
          onClick={() => onChange([...suggestions])}
          className="text-xs text-primary-600 hover:text-primary-800 underline underline-offset-2"
        >
          {suggestions.join(' · ')}
        </button>
      )}
    </div>
  );
}
